import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RefreshCcw, Loader2, ShieldCheck, CalendarCheck2 } from "lucide-react";
import { toast } from "sonner";
import { ordinal } from "@/components/settings/ScheduleSettings";

export function AutopayCard({ onChange }) {
  const [ap, setAp] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/tenant/autopay").then((r) => setAp(r.data)).catch(() => setAp({ active: false, status: "error" }));
  useEffect(() => { load(); }, []);

  const start = async () => {
    setBusy("start");
    try {
      const { data } = await api.post("/tenant/autopay/subscribe", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiErr(e)); setBusy(""); }
  };
  const cancel = async () => {
    setBusy("cancel");
    try { await api.post("/tenant/autopay/cancel"); toast.success("Auto-pay turned off"); await load(); onChange?.(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(""); }
  };

  if (!ap || ap.status === "no_lease") return null;
  const dueDay = ap.due_day || 1;

  return (
    <div className={`rounded-2xl border p-6 ${ap.active ? "border-emerald-200 bg-emerald-50/60" : "border-slate-200 bg-white"}`} data-testid="tenant-autopay-card">
      <div className="flex items-center gap-2 mb-2">
        <span className={`h-9 w-9 rounded-lg flex items-center justify-center ${ap.active ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-600"}`}><RefreshCcw className="h-5 w-5" /></span>
        <h2 className="font-display font-bold text-lg text-slate-900">Auto-pay</h2>
        {ap.active && <span className="ml-auto rounded-full bg-emerald-600 text-white text-[10px] font-bold px-2 py-0.5" data-testid="tenant-autopay-on">ON</span>}
      </div>
      {ap.active ? (
        <>
          <p className="text-sm text-slate-700" data-testid="tenant-autopay-status">
            <b>{fmtMoney(ap.amount)}</b> is charged to your card automatically on the <b>{ordinal(dueDay)}</b> of every month.
            {ap.next_charge && <> Next charge: <b>{new Date(ap.next_charge + "T12:00:00Z").toLocaleDateString()}</b>.</>}
          </p>
          <p className="text-xs text-slate-500 mt-2">Covers your monthly copay only — any late fees or extra balance still need a one-time payment above.</p>
          <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={cancel} disabled={!!busy} data-testid="tenant-autopay-cancel">
            {busy === "cancel" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />} Cancel auto-pay
          </Button>
        </>
      ) : (
        <>
          <p className="text-sm text-slate-600" data-testid="tenant-autopay-status">
            Never miss rent day: your copay of <b>{fmtMoney(ap.copay)}</b> gets charged to your card on the <b>{ordinal(dueDay)}</b> each month.
            {ap.first_charge && <> First charge on <b>{new Date(ap.first_charge + "T12:00:00Z").toLocaleDateString()}</b> — nothing is charged today.</>}
          </p>
          <Button onClick={start} disabled={!!busy || !(ap.copay > 0)} className="mt-4 w-full gap-2 bg-emerald-600 hover:bg-emerald-700" data-testid="tenant-autopay-start">
            {busy === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarCheck2 className="h-4 w-4" />} Turn on auto-pay
          </Button>
          <p className="text-[11px] text-slate-400 mt-2 flex items-center gap-1"><ShieldCheck className="h-3 w-3" /> Secure card setup by Stripe · cancel anytime</p>
        </>
      )}
    </div>
  );
}
